import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/auth.service';
import axios from 'axios';
import TeamPresence from './TeamPresence';
import { signalPresenceLogout } from '../utils/presence';
import { extractUserAccesses as collectAccesses } from '../utils/userAccesses';
import { fetchUnreadPatchCount as loadUnreadPatchCount, PATCH_NOTES_CHANGED } from '../utils/patchNotes';
import { useMercureRefresh } from '../utils/mercure';
/* ── EVOLIS (module détachable — voir EVOLIS-MODULE.md) ── */
import EvolisSidebarButton from '../evolis/EvolisSidebarButton';
/* ── fin EVOLIS ── */
import '../styles/Sidebar.css';
import logoSpot from '../LogoSpot/spot-logo-fond-noir.png';
import nomSpot from '../LogoSpot/spot-nom-fond-noir.png';
import {
    LayoutDashboard,
    LayoutGrid,
    Sparkles,
    Calendar,
    Package,
    FolderOpen,
    Users,
    Warehouse,
    ClipboardCheck,
    Truck,
    PackageCheck,
    User,
    Building2,
    Shield,
    TrendingUp,
    Settings,
    Lock,
    LogOut,
    Briefcase,
    X,
    Sun,
    Moon,
    Network,
    KanbanSquare,
    MessageSquarePlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Mapping des routes vers les permissions d'accès
const ROUTE_ACCESS_MAP = {
    '/': 'access_dashboard',
    '/nouveautes': 'access_nouveautes',
    '/retours': 'access_retours',
    '/admin/events': 'access_events',
    '/admin/salons': 'access_salons',
    '/admin/materials': 'access_materials',
    '/admin/categories': 'access_categories',
    '/admin/clients': 'access_clients',
    '/admin/parcs': 'access_parcs',
    '/admin/validation-devis': 'access_validation_devis',
    '/admin/preparation': 'access_preparation_logistique',
    '/admin/suivi-retours': 'access_suivi_retours',
    '/admin/users': 'access_users',
    '/admin/entities': 'access_entities',
    '/admin/roles': 'access_roles',
    '/admin/statistiques': 'access_statistiques',
    '/admin/repartition-entites': 'access_repartition',
    '/admin/projets': 'access_projets',
    '/admin/projets-parametres': 'access_projets_parametres',
    '/admin/contacts': 'access_contacts',
    '/admin/devtool': 'access_devtool',
    '/admin/equipe-planning': 'access_equipe_planning',
    '/admin/planning-manager': 'access_planning_manager',
};

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [userAccesses, setUserAccesses] = useState([]);
    const [unreadPatchCount, setUnreadPatchCount] = useState(0);
    
    // État pour le mobile menu
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // Thème dark/light
    const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    }, [isDark]);

    const toggleTheme = () => setIsDark(d => !d);

    // Mode présence élargie (permission presence_toutes_entites) : le panneau
    // Équipe montre toutes les entités au lieu de la seule entité principale.
    const [adminMode, setAdminMode] = useState(() => localStorage.getItem('adminMode') === '1');
    const toggleAdminMode = () => setAdminMode(v => {
        localStorage.setItem('adminMode', v ? '0' : '1');
        return !v;
    });

    // État pour le modal de changement de mot de passe
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            const currentUser = authService.getCurrentUser();
            if (currentUser) {
                try {
                    const response = await axios.get(`${API_URL}/users/${currentUser.id || currentUser.userId}`, { 
                        withCredentials: true,
                        headers: { 'Accept': 'application/ld+json' }
                    });
                    setUser(response.data);
                    
                    // Extraire les accès depuis les permissions du rôle
                    const accesses = extractUserAccesses(response.data);
                    setUserAccesses(accesses);

                    refreshUnreadPatchCount(currentUser.id || currentUser.userId);
                } catch (error) {
                    setUser(currentUser);
                    setUserAccesses(Object.values(ROUTE_ACCESS_MAP));
                }
            }
        };
        fetchUser();

        // Horloge : les secondes ne sont plus affichées, on ne re-rend
        // la sidebar qu'au changement de minute (même référence sinon)
        const timer = setInterval(() => {
            setCurrentTime(prev => {
                const now = new Date();
                return now.getMinutes() !== prev.getMinutes() ? now : prev;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const refreshUnreadPatchCount = async (userId) => {
        try {
            const count = await loadUnreadPatchCount(userId);
            setUnreadPatchCount(count);
        } catch {
            setUnreadPatchCount(0);
        }
    };

    useEffect(() => {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
            refreshUnreadPatchCount(currentUser.id || currentUser.userId);
        }
    }, [location.pathname]);

    useEffect(() => {
        const onChanged = () => {
            const currentUser = authService.getCurrentUser();
            if (currentUser) refreshUnreadPatchCount(currentUser.id || currentUser.userId);
        };
        window.addEventListener(PATCH_NOTES_CHANGED, onChanged);
        return () => window.removeEventListener(PATCH_NOTES_CHANGED, onChanged);
    }, []);

    useMercureRefresh(['patch_notes'], () => {
        const currentUser = authService.getCurrentUser();
        if (currentUser) refreshUnreadPatchCount(currentUser.id || currentUser.userId);
    }, 800);

    // Gestion Échap pour fermer le modal mot de passe
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showPasswordModal) closePasswordModal();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showPasswordModal]);

    const handlePasswordOverlayMouseDown = (e) => {
        if (e.target === e.currentTarget) closePasswordModal();
    };

    const extractUserAccesses = (userData) => {
        const accesses = collectAccesses(userData);
        if (accesses.length === 0) {
            return Object.values(ROUTE_ACCESS_MAP);
        }
        return accesses;
    };

    const hasAccess = (route) => {
        const requiredAccess = ROUTE_ACCESS_MAP[route];
        if (!requiredAccess) return true;
        return userAccesses.includes(requiredAccess);
    };

    const handleLogout = () => {
        // Prévient le système de présence AVANT d'effacer la session locale,
        // sinon le compte reste affiché « en ligne » pendant ~3 minutes.
        signalPresenceLogout();
        authService.logout();
        navigate('/login');
    };

    const handlePasswordChange = (e) => {
        setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
        setPasswordError('');
        setPasswordSuccess('');
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');
        
        const oldPwd = passwordForm.oldPassword.trim();
        const newPwd = passwordForm.newPassword.trim();
        const confirmPwd = passwordForm.confirmPassword.trim();
        
        if (!oldPwd) {
            setPasswordError('Veuillez saisir votre mot de passe actuel');
            return;
        }
        if (!newPwd) {
            setPasswordError('Veuillez saisir un nouveau mot de passe');
            return;
        }
        if (newPwd.length < 6) {
            setPasswordError('Le nouveau mot de passe doit contenir au moins 6 caractères');
            return;
        }
        if (newPwd !== confirmPwd) {
            setPasswordError('Le nouveau mot de passe et sa confirmation ne correspondent pas');
            return;
        }
        if (oldPwd === newPwd) {
            setPasswordError('Votre nouveau mot de passe doit être différent de votre mot de passe actuel');
            return;
        }

        setPasswordLoading(true);

        try {
            await axios.post(`${API_URL}/change-password`, {
                oldPassword: oldPwd,
                newPassword: newPwd
            }, {
                withCredentials: true,
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            setPasswordSuccess('Mot de passe modifié avec succès !');
            setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
            
            setTimeout(() => {
                setShowPasswordModal(false);
                setPasswordSuccess('');
            }, 2000);

        } catch (error) {
            if (error.response?.status === 400) {
                setPasswordError('Mot de passe actuel incorrect');
            } else if (error.response?.data?.message) {
                setPasswordError(error.response.data.message);
            } else {
                setPasswordError('Erreur lors du changement de mot de passe');
            }
        }

        setPasswordLoading(false);
    };

    const closePasswordModal = () => {
        setShowPasswordModal(false);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError('');
        setPasswordSuccess('');
    };

    if (location.pathname === '/login') {
        return null;
    }

    if (!user) {
        return null;
    }

    const userRole = user.role?.nom || 'Utilisateur';
    const userEntity = user.entityReference?.nom || 'Non assigné';
    const userName = `${user.prenom || ''} ${user.nom || ''}`.trim() || 'Utilisateur';

    const navCategories = [
        {
            title: 'Principal',
            items: [
                { label: 'Tableau de bord', route: '/', icon: 'dashboard', access: 'access_dashboard' },
                { label: 'Nouveautés', route: '/nouveautes', icon: 'sparkle', badge: unreadPatchCount },
            ]
        },
        {
            title: 'Gestion',
            items: [
                { label: 'Planning', route: '/admin/events', icon: 'calendar', access: 'access_events' },
                { label: 'Salons', route: '/admin/salons', icon: 'layout-grid', access: 'access_salons' },
                { label: 'Matériels', route: '/admin/materials', icon: 'box', access: 'access_materials' },
                { label: 'Catégories', route: '/admin/categories', icon: 'folder', access: 'access_categories' },
                { label: 'Clients', route: '/admin/clients', icon: 'users', access: 'access_clients' },
                { label: 'Contacts externes', route: '/admin/contacts', icon: 'users', access: 'access_contacts' },
                { label: 'Parcs', route: '/admin/parcs', icon: 'warehouse', access: 'access_parcs' },
            ]
        },
        {
            title: 'Projets',
            items: [
                { label: 'Formulaires Projets', route: '/admin/projets', icon: 'briefcase', access: 'access_projets' },
                { label: 'Paramètres Projets', route: '/admin/projets-parametres', icon: 'settings', access: 'access_projets_parametres' },
            ]
        },
        {
            title: 'Équipe',
            items: [
                { label: 'Équipe & planning', route: '/admin/equipe-planning', icon: 'organisation', access: 'access_equipe_planning' },
                { label: 'Planning — assignation', route: '/admin/planning-manager', icon: 'planning-assign', access: 'access_planning_manager' },
            ]
        },
        {
            title: 'Logistique',
            items: [
                { label: 'Validation Devis', route: '/admin/validation-devis', icon: 'clipboard-check', access: 'access_validation_devis' },
                { label: 'Préparation', route: '/admin/preparation', icon: 'truck', access: 'access_preparation_logistique' },
                { label: 'Suivi Retours', route: '/admin/suivi-retours', icon: 'package', access: 'access_suivi_retours' },
            ]
        },
        {
            title: 'Administration',
            items: [
                { label: 'Utilisateurs', route: '/admin/users', icon: 'user', access: 'access_users' },
                { label: 'Entités', route: '/admin/entities', icon: 'building', access: 'access_entities' },
                { label: 'Rôles', route: '/admin/roles', icon: 'shield', access: 'access_roles' },
            ]
        },
        {
            title: 'Direction',
            items: [
                { label: 'Statistiques', route: '/admin/statistiques', icon: 'chart', access: 'access_statistiques' },
                { label: 'Répartition Entités', route: '/admin/repartition-entites', icon: 'building', access: 'access_repartition' },
            ]
        },
        {
            title: 'Développement',
            items: [
                { label: 'DevTool', route: '/admin/devtool', icon: 'devtool', access: 'access_devtool' },
            ]
        }
    ];

    const filteredNavCategories = navCategories
        .map(category => ({
            ...category,
            items: category.items.filter(item => hasAccess(item.route))
        }))
        .filter(category => category.items.length > 0);

    const isActive = (route) => {
        if (route === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(route);
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('fr-FR', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short'
        });
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit'
        });
    };

    return (
        <>
            {/* Bouton flottant mobile pour ouvrir le menu */}
            <button 
                className="mobile-menu-fab"
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Ouvrir le menu"
            >
                <LayoutDashboard size={24} />
            </button>

            {/* Overlay mobile */}
            {isMobileMenuOpen && (
                <div 
                    className="mobile-menu-overlay"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            <aside className={`sidebar-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
                {/* En-tête : marque + date/heure */}
                <div className="sidebar-header">
                    <div className="sidebar-brand">
                        <img src={logoSpot} alt="" className="sidebar-brand-logo" aria-hidden="true" />
                        <img src={nomSpot} alt="SPOT" className="sidebar-brand-nom" />
                    </div>
                    <div className="sidebar-datetime">
                        <span className="sidebar-time">{formatTime(currentTime)}</span>
                        <span className="sidebar-date">{formatDate(currentTime)}</span>
                    </div>
                </div>

                {/* Profil utilisateur et entité */}
                <div className="sidebar-profile-section">
                    <div className="sidebar-profile-top">
                        <div className="sidebar-user-details">
                            <span className="sidebar-username">{userName}</span>
                            <span className="sidebar-role">{userRole}</span>
                        </div>
                    </div>
                    <div className="sidebar-entity-badge">
                        <span className="entity-icon"></span>
                        <span className="entity-name">{userEntity}</span>
                        {authService.peutPresenceToutes() && (
                            <button
                                type="button"
                                className={`sidebar-admin-mode${adminMode ? ' on' : ''}`}
                                onClick={toggleAdminMode}
                                title={adminMode
                                    ? 'Mode admin activé : le panneau Équipe montre toutes les entités'
                                    : 'Mode admin désactivé : le panneau Équipe montre votre entité'}
                            >
                                <span className="sidebar-admin-mode-label">admin</span>
                                <span className="sidebar-admin-mode-switch" aria-hidden="true">
                                    <span className="sidebar-admin-mode-knob" />
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Navigation par catégories */}
                <nav className="sidebar-menu">
                    {filteredNavCategories.map((category, catIndex) => (
                        <div key={catIndex} className="sidebar-category">
                            <div className="sidebar-category-title">{category.title}</div>
                            <div className="sidebar-category-items">
                                {category.items.map((item, index) => (
                                    <button
                                        key={index}
                                        className={`sidebar-item ${isActive(item.route) ? 'active' : ''}`}
                                        onClick={() => { navigate(item.route); setIsMobileMenuOpen(false); }}
                                        aria-label={item.badge > 0 ? `${item.label}, ${item.badge} non lu${item.badge > 1 ? 'es' : 'e'}` : item.label}
                                    >
                                        <span className="sidebar-icon-wrap">
                                            <span className="sidebar-icon">{getIcon(item.icon)}</span>
                                            {item.badge > 0 && <span className="sidebar-pip" aria-hidden="true" />}
                                        </span>
                                        <span className="sidebar-label">{item.label}</span>
                                        {item.badge > 0 && (
                                            <span className="sidebar-badge">{item.badge}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Équipe en ligne (présence temps réel) */}
                <TeamPresence adminMode={adminMode && authService.peutPresenceToutes()} />

                {/* ── EVOLIS : chat en direct, en bas de la sidebar ── */}
                <EvolisSidebarButton />
                {/* ── fin EVOLIS ── */}

                {/* Footer : retours / thème / mot de passe / déconnexion */}
                <div className="sidebar-footer">
                    {hasAccess('/retours') && (
                        <button
                            className={`sidebar-retours${isActive('/retours') ? ' active' : ''}`}
                            onClick={() => { navigate('/retours'); setIsMobileMenuOpen(false); }}
                            title="Retours"
                            aria-label="Retours"
                            aria-current={isActive('/retours') ? 'page' : undefined}
                        >
                            <span className="sidebar-icon">{getIcon('retours')}</span>
                        </button>
                    )}
                    <button
                        className="sidebar-theme-toggle"
                        onClick={toggleTheme}
                        title={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
                    >
                        <span className="sidebar-icon">
                            {isDark ? <Sun size={18} /> : <Moon size={18} />}
                        </span>
                    </button>
                    <button
                        className="sidebar-security"
                        onClick={() => setShowPasswordModal(true)}
                        title="Changer mon mot de passe"
                    >
                        <span className="sidebar-icon">{getIcon('security')}</span>
                    </button>
                    <button className="sidebar-logout" onClick={handleLogout} title="Déconnexion">
                        <span className="sidebar-icon">{getIcon('logout')}</span>
                    </button>
                </div>
            </aside>

            {/* Modal de changement de mot de passe */}
            {showPasswordModal && (
                <div className="password-modal-overlay" onMouseDown={handlePasswordOverlayMouseDown}>
                    <div className="password-modal">
                        <div className="password-modal-header">
                            <div className="password-modal-icon">
                                <span>{getIcon('security')}</span>
                            </div>
                            <h2>Changer mon mot de passe</h2>
                            <button className="password-modal-close" onClick={closePasswordModal}>
                                <X size={16} />
                            </button>
                        </div>

                        <form onSubmit={handlePasswordSubmit} className="password-modal-form" autoComplete="off">
                            {passwordError && (
                                <div className="password-alert error">
                                    <span className="alert-icon"></span>
                                    {passwordError}
                                </div>
                            )}
                            {passwordSuccess && (
                                <div className="password-alert success">
                                    <span className="alert-icon"></span>
                                    {passwordSuccess}
                                </div>
                            )}

                            <div className="password-form-group">
                                <label>Mot de passe actuel</label>
                                <input
                                    type="password"
                                    name="oldPassword"
                                    value={passwordForm.oldPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Saisissez votre mot de passe actuel"
                                    autoComplete="current-password"
                                />
                            </div>

                            <div className="password-form-group">
                                <label>Nouveau mot de passe</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    value={passwordForm.newPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Saisissez votre nouveau mot de passe"
                                    autoComplete="new-password"
                                />
                                <span className="password-hint">Minimum 6 caractères</span>
                            </div>

                            <div className="password-form-group">
                                <label>Confirmer le nouveau mot de passe</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={passwordForm.confirmPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Confirmez votre nouveau mot de passe"
                                    autoComplete="new-password"
                                />
                            </div>

                            <div className="password-modal-actions">
                                <button 
                                    type="button" 
                                    className="btn-cancel"
                                    onClick={closePasswordModal}
                                >
                                    Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-submit"
                                    disabled={passwordLoading}
                                >
                                    {passwordLoading ? 'Modification...' : 'Modifier'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

// Fonction pour les icônes Lucide (sobres et monochromes)
const getIcon = (name) => {
    const iconSize = 20;
    const icons = {
        dashboard: <LayoutDashboard size={iconSize} />,
        calendar: <Calendar size={iconSize} />,
        box: <Package size={iconSize} />,
        folder: <FolderOpen size={iconSize} />,
        users: <Users size={iconSize} />,
        warehouse: <Warehouse size={iconSize} />,
        user: <User size={iconSize} />,
        building: <Building2 size={iconSize} />,
        shield: <Shield size={iconSize} />,
        logout: <LogOut size={iconSize} />,
        chart: <TrendingUp size={iconSize} />,
        security: <Lock size={iconSize} />,
        devtool: <Settings size={iconSize} />,
        sparkle: <Sparkles size={iconSize} />,
        retours: <MessageSquarePlus size={iconSize} />,
        truck: <Truck size={iconSize} />,
        package: <PackageCheck size={iconSize} />,
        'clipboard-check': <ClipboardCheck size={iconSize} />,
        briefcase: <Briefcase size={iconSize} />,
        settings: <Settings size={iconSize} />,
        'layout-grid': <LayoutGrid size={iconSize} />,
        organisation: <Network size={iconSize} />,
        'planning-assign': <KanbanSquare size={iconSize} />
    };
    return icons[name] || <Package size={iconSize} />;
};

export default Sidebar;
